import { CSSProperties, useMemo, useState } from 'react'
import { clsx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import useScreen from 'Hooks/useScreen'
import useOrganizationV2 from 'Hooks/useOrganizationV2'
import useUser from 'Hooks/useUser'
import PageSymphony from 'elements/PageSymphony'
import CardSymphony from 'elements/CardSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import { Body2, Caption } from 'components/shareable/Typography'
import TableElement, { ColDef } from 'elements/TableElement'
import Icon from 'elements/Icon'
import AddMemberModal from 'pages/OrganizationPage/componentsOrganization/AddMemberModal/AddMemberModal'
import EditMemberModalV2 from 'pages/OrganizationPage/componentsOrganization/EditMemberModalV2'
import ModalSymphony from 'elements/ModalSymphony'
import { toastSuccess } from 'modules/Toast'
import { MEMBER_ROLES_INFO, OrganizationRole, TEAM_PLAN_ADDITIONAL_SEAT_PRICE, TEAM_PLAN_INCLUDED_SEATS } from 'modules/Const'

interface TeamUsersPageProps {
  className?: string
  style?: CSSProperties
}

const TeamUsersPage = ({
  className,
  style
}: TeamUsersPageProps) => {
  const { mobileView } = useScreen()
  const { organization, organizationUsers, deleteOrganizationUser, hasOrganizationAdminAccess, isLoadingDeleteMember } = useOrganizationV2()
  const { currentUser } = useUser()
  const [isMemberModalOpen, setIsMemberModalOpen] = useState<boolean>(false)
  const [isEditMemberModalOpen, setIsEditMemberModalOpen] = useState<boolean>(false)
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState<any>(null)
  const [memberToRemove, setMemberToRemove] = useState<{
    email: string
    id: number
    brands: Array<{ id: number; name: string }>
  } | null>(null)

  const renderEmptyIcon = () => {
    return (
      <div className='emptyIconContainer' style={sty.emptyIconContainer}>
        <div className='emptyIconCenter' style={sty.emptyIconCenter} />
      </div>
    )
  }

  const renderTeamFooter = () => {
    if ((organizationUsers?.length || 0) >= TEAM_PLAN_INCLUDED_SEATS) {
      return null
    }

    let remainingSeats = TEAM_PLAN_INCLUDED_SEATS - (organizationUsers?.length || 0)
    return (
      <div className='teamFooterContainer' style={sty.teamFooterContainer}>
        <div className='flex gap-4 items-center'>
          {renderEmptyIcon()}
          <div className='flex flex-col gap-1'>
            <Body2 color={Colors.greyDark}>
              You have {remainingSeats} user seat{remainingSeats > 1 ? 's' : ''} available
            </Body2>
            <Caption color={Colors.greyDark}>
              {TEAM_PLAN_INCLUDED_SEATS} seats are included with the team plan, +${TEAM_PLAN_ADDITIONAL_SEAT_PRICE} / month for additional
            </Caption>
          </div>
        </div>
        <ButtonSymphony
          className='buttonAddMemberAvailableSeat'
          onClick={() => setIsMemberModalOpen(true)}
          variant='text'
        >
          Add member
        </ButtonSymphony>
      </div>
    )
  }

  const handleRemoveMember = async () => {
    if (!organization?.id || !memberToRemove) {
      return
    }

    try {
      await deleteOrganizationUser({
        organizationId: organization.id,
        userId: memberToRemove.id
      })

      toastSuccess(`Successfully removed ${memberToRemove.email} from your team`)
      setMemberToRemove(null)
    } catch (error) {
      console.error('Failed to remove member:', error)
    }
  }

  const renderBrandsList = (brands: Array<{ id: number; name: string }>) => {
    if (!brands || brands.length === 0) {
      return (
        <Body2 color={Colors.greyDark} style={sty.noBrandsText}>
          This member doesn't have access to any brands.
        </Body2>
      )
    }

    return (
      <div className='brandsList' style={sty.brandsList}>
        {brands.map((brand) => (
          <div key={brand.id} className='brandItem' style={sty.brandItem}>
            <Body2 color={Colors.greyDark}>
              • {brand.name}
            </Body2>
          </div>
        ))}
      </div>
    )
  }

  const renderRemoveMemberModal = () => {
    if (!memberToRemove) {
      return null
    }

    return (
      <ModalSymphony
        className='removeMemberConfirmationModal'
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        title='Remove Team Member'
        subtitle={
          <div className='modalSubtitle' style={sty.modalSubtitle}>
            <Body2 color={Colors.greyDark} style={sty.confirmationText}>
              Are you sure you want to remove <strong>{memberToRemove.email}</strong> from your team?
              They will lose full access to these brands:
            </Body2>
            {renderBrandsList(memberToRemove.brands)}
          </div>
        }
        buttonLeftText='Cancel'
        onClickButtonLeft={() => setMemberToRemove(null)}
        buttonRightText='Remove'
        buttonRightColor='red'
        onClickButtonRight={handleRemoveMember}
        buttonRightIsLoading={isLoadingDeleteMember}
        buttonRightLoadingText='Removing...'
        size='medium'
      />
    )
  }

  return (
    <>
      <PageSymphony
        className={clsx('teamUsersPage', className)}
        style={style}
        isContent
      >
        <CardSymphony
          className='teamUsersCard'
          title='Team Members'
          subtitle='Manage your team members and their permissions.'
          additionalButtons={
            <ButtonSymphony
              className='addTeamMemberButton'
              onClick={() => setIsMemberModalOpen(true)}
              iconLeft='plus'
              iconLeftSize={16}
            >
              Add Team Member
            </ButtonSymphony>
          }
        ></CardSymphony>
        <CardSymphony className='teamUsersCard'>
          <TableElement
            className='teamUsersTable'
            rowData={(organizationUsers || []).map(ou => {
              const isOwner = ou.role === OrganizationRole.OWNER
              const removable = !!currentUser?.id && currentUser.id !== ou.user_id && !isOwner
              return {
                id: ou.user_id,
                email: ou.email,
                name: ou.name || '-',
                role: ou.role,
                invitation_status: ou.invitation_status,
                removable,
              }
            })}
            columnDefs={[
              {
                field: 'email',
                title: 'Email',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 260 : undefined,
              },
              {
                field: 'role',
                title: 'Role',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 140 : undefined,
                renderCell: (row: any) => {
                  const isOwner = row.role === OrganizationRole.OWNER
                  const label = MEMBER_ROLES_INFO[row.role as OrganizationRole]?.title
                  return (
                    <div className='teamUsersRoleCell' style={{ display: 'flex', alignItems: 'center', gap: 4, color: Colors.greyDark }}>
                      <div>{label} {(row.invitation_status === 'pending' ? "(Invited)" : '')}</div>
                      {isOwner &&
                        <Icon className='teamUsersRoleIcon' icon='crown' color={Colors.gold} />
                      }
                    </div>
                  )
                }
              },
              {
                field: 'actions',
                title: 'Actions',
                flex: mobileView ? 0 : 1,
                width: mobileView ? 120 : undefined,
                renderCell: (row: any) => {
                  if (!row.removable || !hasOrganizationAdminAccess()) {
                    return <div>-</div>
                  }

                  const handleEditAccessClick = () => {
                    // Find the organization user to pass to the edit modal
                    const orgUser = organizationUsers?.find(ou => ou.user_id === row.id)
                    if (orgUser) {
                      setSelectedMemberForEdit(orgUser)
                      setIsEditMemberModalOpen(true)
                    }
                  }

                  const handleRemoveClick = () => {
                    // Find the organization user to get their brands
                    const orgUser = organizationUsers?.find(ou => ou.user_id === row.id)
                    const userBrands = orgUser?.brands ?
                      (Array.isArray(orgUser.brands) ?
                        orgUser.brands.map((brand: any) => ({ id: brand.id, name: brand.name })) :
                        [{ id: orgUser.brands.id, name: orgUser.brands.name }]
                      ) : []

                    setMemberToRemove({
                      email: row.email,
                      id: row.id,
                      brands: userBrands
                    })
                  }

                  return (
                    <div className='teamUsersActions' style={sty.teamUsersActions}>
                      <ButtonSymphony
                        className='teamUsersEditButton'
                        variant='text'
                        onClick={handleEditAccessClick}
                      >
                        Edit Access
                      </ButtonSymphony>
                    </div>
                  )
                }
              }
            ] as ColDef[]}
            pagination={false}
            rowsTitle='users'
            isHover={false}
          />
          {renderTeamFooter()}
        </CardSymphony>
      </PageSymphony>
      <AddMemberModal
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
      />
      <EditMemberModalV2
        isOpen={isEditMemberModalOpen}
        onClose={() => {
          setIsEditMemberModalOpen(false)
          setSelectedMemberForEdit(null)
        }}
        selectedMember={selectedMemberForEdit}
        onRemoveMember={() => {
          // Close edit modal and open remove modal
          setIsEditMemberModalOpen(false)
          if (selectedMemberForEdit) {
            const userBrands = selectedMemberForEdit?.brands ?
              (Array.isArray(selectedMemberForEdit.brands) ?
                selectedMemberForEdit.brands.map((brand: any) => ({ id: brand.id, name: brand.name })) :
                [{ id: selectedMemberForEdit.brands.id, name: selectedMemberForEdit.brands.name }]
              ) : []
            
            setMemberToRemove({
              email: selectedMemberForEdit.email || `${selectedMemberForEdit.first_name} ${selectedMemberForEdit.last_name}`,
              id: selectedMemberForEdit.user_id,
              brands: userBrands
            })
          }
          setSelectedMemberForEdit(null)
        }}
      />
      {renderRemoveMemberModal()}
    </>
  )
}

const sty: Record<string, CSSProperties> = {
  teamFooterContainer: {
    width: '100%',
    borderRadius: 8,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 24,
    paddingRight: 24,
    marginTop: 16,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.greyLighter,
  },
  teamUsersActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24
  },
  emptyIconContainer: {
    backgroundColor: Colors.PAPER_DIVIDER_CONTAINER,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
  },
  emptyIconCenter: {
    backgroundColor: Colors.PAPER_DIVIDER_CONTAINER_DARK,
    borderRadius: '50%',
    width: 8,
    height: 8,
  },
  modalSubtitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  confirmationText: {
    lineHeight: 1.5,
  },
  brandsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingLeft: 16,
  },
  brandItem: {
    display: 'flex',
    alignItems: 'center',
  },
  noBrandsText: {
    fontStyle: 'italic',
    paddingLeft: 16,
  },
}

export default TeamUsersPage
